// src/components/reports/ComplianceTab.jsx
//
// Renders the `complianceGuide` field from the paid report.
// Graceful degradation: if `complianceGuide` is missing/empty, shows a
// friendly notice instead of crashing. Never calls the network directly.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ShieldAlert, FileLock, BookOpen, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronUp, Wrench, Clock, Download, Info,
} from 'lucide-react';
import { formatIndustryLabel } from '../../utils/reportHelpers';

const cadenceLabel = (c) => {
  switch ((c || '').toLowerCase()) {
    case 'quarterly':       return 'Every 3 months';
    case 'semi-annually':
    case 'semiannually':    return 'Every 6 months';
    case 'annually':        return 'Every 12 months';
    case 'per-deployment':
    case 'per_deployment':  return 'Before each new AI tool deployment';
    default:                return c || 'Annually';
  }
};

const SectionCard = ({ title, icon: Icon, accent = 'blue', children }) => (
  <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
    <CardContent className="p-6">
      <h3 className={`text-base font-semibold text-${accent}-400 mb-3 flex items-center gap-2`}>
        <Icon className="w-4 h-4" /> {title}
      </h3>
      <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
        {children}
      </div>
    </CardContent>
  </Card>
);

export const ComplianceTab = ({ complianceGuide, industry, subVertical }) => {
  const [expandedScenario, setExpandedScenario] = useState(null);

  if (!complianceGuide || !Array.isArray(complianceGuide.regulations) || complianceGuide.regulations.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
        <FileLock className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="text-lg">Compliance guide unavailable for this report.</p>
        <p className="text-sm mt-2">
          Upgrade your report or reach out to support — we can regenerate the compliance section on request.
        </p>
      </div>
    );
  }

  const {
    regulations = [],
    data_handling = '',
    consent_and_disclosure = '',
    audit_trail = '',
    risk_scenarios = [],
    tool_specific_notes = '',
    recommended_review_cadence = 'annually',
  } = complianceGuide;

  // R40 (Sarah R36e verdict): Compliance tab heading rendered "Real
  // Estate Compliance Guide" + body intro "what applies to real estate"
  // to PM operators. Same sub_vertical override as the other tabs.
  const niceIndustry = formatIndustryLabel(
    industry || complianceGuide.industry || 'your industry',
    'your industry',
    subVertical,
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-6"
    >
      {/* ── Header banner ── */}
      <div className="bg-amber-500/8 border border-amber-500/25 rounded-xl p-5 flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">
            {niceIndustry} Compliance Guide
          </h2>
          <p className="text-sm text-slate-300">
            Adding AI to your stack doesn't create new regulations — it changes which ones you
            need to think about. This guide walks through what applies to {niceIndustry.toLowerCase()}, what
            changes when AI vendors touch your data, and what to put in writing before you connect any tool.
          </p>
          <p className="text-xs text-amber-300/80 mt-3 italic">
            This is general guidance, not legal advice. Consult a professional for your specific situation.
          </p>
        </div>
      </div>

      {/* ── Regulations grid ── */}
      <div>
        <h3 className="text-xl font-semibold text-blue-400 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" /> Regulations That Apply
        </h3>
        <div className="space-y-4">
          {regulations.map((reg, idx) => (
            <Card key={reg.name || idx} className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-start gap-3">
                    <FileLock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-base font-semibold text-white">{reg.name}</h4>
                      {reg.summary && (
                        <p className="text-sm text-slate-400 mt-1">{reg.summary}</p>
                      )}
                    </div>
                  </div>
                  <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] shrink-0">
                    Applies to you
                  </Badge>
                </div>

                {reg.ai_implications && (
                  <div className="mt-4 border-t border-white/8 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1.5">
                      What changes when you add AI
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">{reg.ai_implications}</p>
                  </div>
                )}

                {Array.isArray(reg.action_items) && reg.action_items.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#3b82f6] mb-2">
                      Action items
                    </p>
                    <ul className="space-y-1.5">
                      {reg.action_items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Data handling ── */}
      {data_handling && (
        <SectionCard title="Data Handling" icon={FileLock} accent="blue">
          {data_handling}
        </SectionCard>
      )}

      {/* ── Consent & disclosure ── */}
      {consent_and_disclosure && (
        <SectionCard title="Consent & Disclosure" icon={Info} accent="blue">
          {consent_and_disclosure}
        </SectionCard>
      )}

      {/* ── Audit trail ── */}
      {audit_trail && (
        <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-base font-semibold text-blue-400 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Audit Trail & Retention
              </h3>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full
                           bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 text-[#3b82f6] transition-colors"
                title="Sample retention policy — ask support for the current template"
              >
                <Download className="w-3.5 h-3.5" /> Sample retention policy
              </a>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{audit_trail}</p>
          </CardContent>
        </Card>
      )}

      {/* ── Risk scenarios (accordion) ── */}
      {Array.isArray(risk_scenarios) && risk_scenarios.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Risk Scenarios to Watch
          </h3>
          <div className="space-y-2">
            {risk_scenarios.map((scenario, idx) => {
              const isOpen = expandedScenario === idx;
              // Split "description ... Mitigation: xyz" into two parts for readability
              const match = String(scenario).split(/mitigation[:\s]/i);
              const headline = match[0]?.trim() || String(scenario);
              const mitigation = match[1]?.trim();
              return (
                <Card key={idx} className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
                  <button
                    onClick={() => setExpandedScenario(isOpen ? null : idx)}
                    className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-200 leading-relaxed">{headline}</span>
                    </div>
                    <span className="text-slate-500 flex-shrink-0">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </button>
                  <AnimatePresence>
                    {isOpen && mitigation && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="px-4 pb-4 pt-1 border-t border-white/8">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#3b82f6] mb-1.5">
                            Mitigation
                          </p>
                          <p className="text-sm text-slate-300 leading-relaxed">{mitigation}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tool-specific notes ── */}
      {tool_specific_notes && (
        <SectionCard title="Tool-Specific Notes" icon={Wrench} accent="blue">
          {tool_specific_notes}
        </SectionCard>
      )}

      {/* ── Review cadence footer ── */}
      <div className="flex items-center justify-between text-xs text-slate-500 border-t border-white/8 pt-4">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          <span>
            Recommended review cadence:{' '}
            <span className="text-slate-300 font-medium">{cadenceLabel(recommended_review_cadence)}</span>
          </span>
        </div>
        <span className="italic">AI Workshop LLC — general guidance, not legal advice</span>
      </div>
    </motion.div>
  );
};

export default ComplianceTab;
